import Image from "next/image";
import React, { useState } from "react";
import styled from "styled-components";
import { SLOT_PROVIDER, SLOT_PROVIDER_READABLE } from "types/slots/constants";

type Props = {
  onProviderSelect?: (provider: string) => void;
  activeProviders?: string[];
  providers: string[];
};

const preview = ["COMBAT_ROYALE", "SMASH_GAMES"];

function ProviderLogo({ img, name }: { img: string; name: string }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <span className="logo-fallback">{SLOT_PROVIDER_READABLE[img as SLOT_PROVIDER] || name}</span>
    );
  }

  return (
    <Image
      src={`/images/slots/providers/${img}.png`}
      fill
      sizes="(max-width: 768px) 140px, (max-width: 1200px) 160px, 180px"
      style={{ objectFit: "contain" }}
      alt={name}
      onError={() => setFailed(true)}
    />
  );
}

function Providers({ onProviderSelect, activeProviders = [], providers = [] }: Props) {
  const ren = [
    ...providers.map((i) => ({ name: i, img: i, disabled: false })),
    ...preview.map((i) => ({ name: "Coming Soon", img: i, disabled: true })),
  ];

  return (
    <ProviderGrid>
      {ren?.map(({ name, img, disabled = false }) => {
        return (
          <StyledCard
            className={activeProviders?.includes(name) ? "active" : ""}
            key={img}
            onClick={() => {
              onProviderSelect?.(name);
            }}
            disabled={disabled}
          >
            <div className="img-wrapper">
              <div className="img">
                <ProviderLogo img={img} name={name} />
              </div>
              {disabled && (
                <div className="img-hidden">
                  <Image
                    alt="lock"
                    src="/images/games/lock.svg"
                    width={14}
                    height={14}
                    style={{ marginRight: 8 }}
                  />
                </div>
              )}
            </div>

            <div className="name">{SLOT_PROVIDER_READABLE[name as SLOT_PROVIDER] || name}</div>
          </StyledCard>
        );
      })}
    </ProviderGrid>
  );
}

const ProviderGrid = styled.div`
  margin-top: 1rem;
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));

  gap: 16px;
  @media (max-width: 940px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
`;

const StyledCard = styled.button`
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: var(--Dark-Midnight, #141722);
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid transparent;
  transition: all 0.2s ease-in-out;

  &:hover,
  &.active {
    cursor: pointer;
    border: 1px solid var(--pure-sky-100, #00b2ff);
    transform: scale(1.02);
    box-shadow: 0px 0px 0px 2px rgba(0, 179, 255, 0.1);

    .name {
      text-shadow: 0px 0px 3px #00b3ff92;
      border-color: var(--pure-sky-100, #00b2ff);
    }
  }

  &:active {
    transform: scale(1.05);
  }

  .disabled,
  &:disabled {
    pointer-events: none;
  }

  .img {
    position: relative;
    height: 40px;
    max-width: 100px;
    width: 100%;
    padding: 10px;
    margin: 10px;
  }
  .logo-fallback {
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    font-family: var(--font-family-golos);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    color: #d9f4ffbf;
  }
  .img-hidden {
    position: absolute;
    inset: 0;
    /* filter: blur(10px); */
    backdrop-filter: blur(10px);

    img {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .img-wrapper {
    display: flex;
    justify-content: center;
    width: 100%;
    position: relative;
  }
  .name {
    width: 100%;
    border-top: 1px solid var(--diamond-white-5, rgba(217, 244, 255, 0.05));
    padding: 10px;
    display: flex;
    justify-content: center;
    font-family: var(--font-family-golos);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    text-transform: capitalize;
    color: #d9f4ffbf;
    transition: all 0.2s linear;
  }
`;

export default Providers;
